export const projectOverlayData = {
  "Automated Youtube / Tiktok Data Scraper": {
    sections: [
      {
        title: "Overview",
        content: "Automated workflow that scrapes, analyzes, and extracts insights from YouTube and TikTok videos. Combines web scraping with AI-powered thumbnail analysis to identify trending patterns and engagement signals.",
        stats: [
          { label: "Videos per Query", value: "5" },
          { label: "AI Model Analyzer", value: "GPT-4.1-mini" },
          { label: "Export", value: "CSV" }
        ]
      },
      {
        title: "YouTube Workflow",
        type: "steps",
        steps: [
          {
            title: "Search & Scrape",
            description: "Executes Apify YouTube scraper with custom queries (max 5 videos, sort by relevance)"
          },
          {
            title: "Retrieve Dataset",
            description: "Fetches video metadata: thumbnails, titles, descriptions, hashtags"
          },
          {
            title: "AI Thumbnail Analysis",
            description: "GPT-4O-mini analyzes thumbnails for visible text, visual elements, and tone"
          },
          {
            title: "Merge & Analyze",
            description: "Combines data streams, detects patterns, identifies engagement signals, exports to CSV"
          }
        ]
      },
      {
        title: "TikTok Workflow",
        type: "steps",
        steps: [
          {
            title: "Search & Scrape",
            description: "Apify TikTok scraper retrieves videos with cover downloads and hashtag extraction"
          },
          {
            title: "Download Thumbnails",
            description: "Fetches thumbnail images from TikTok CDN in base64 format"
          },
          {
            title: "AI Visual Analysis",
            description: "GPT-4O analyzes TikTok thumbnails for color themes, composition, and audience cues"
          },
          {
            title: "Trend Analysis",
            description: "LLM generates insights on viral patterns, hashtag clusters, and visual trends"
          }
        ]
      },
      {
        title: "Technical Stack",
        type: "tech-list",
        items: [
          { name: "n8n", description: "Workflow automation platform" },
          { name: "Apify", description: "Web scraping infrastructure" },
          { name: "OpenAI GPT-4O", description: "AI vision model for analysis" },
          { name: "HTTP Requests", description: "API integration layer" }
        ]
      },
      {
        title: "Use Cases",
        type: "list",
        items: [
          "Content creators analyzing competitor strategies",
          "Marketing teams tracking trending topics",
          "Brand managers monitoring engagement patterns",
          "Researchers studying social media trends"
        ]
      },
      {
        title: "Output Format",
        type: "list",
        items: [
          "Video titles and descriptions",
          "Hashtag analysis and frequency",
          "Thumbnail visual analysis (text, colors, composition)",
          "Engagement metrics (views, likes, comments)",
          "Trend patterns and insights"
        ]
      },
      {
        title: "Key Features",
        type: "list",
        items: [
          "Dual-platform scraping (YouTube + TikTok)",
          "AI-powered thumbnail and visual analysis",
          "Automated trend detection and pattern recognition",
          "CSV export for data analysis",
          "Scalable workflow with configurable parameters"
        ]
      }
    ]
  },

  "Automated AI Videos Generator": {
    sections: [
      {
        title: "Overview",
        content: "Automated pipeline for generating video content using AI models for visuals, scenes, and scripts. Orchestrates multiple AI services to create complete video narratives from concept to final output.",
        stats: [
          { label: "Platform", value: "n8n" },
          { label: "AI Engine", value: "OpenAI" },
          { label: "Image Gen", value: "GoogleFlow" }
        ]
      },
      {
        title: "Video Generation Workflow",
        type: "steps",
        steps: [
          {
            title: "Script Generation",
            description: "LLM creates video script based on input topic or prompt with scene breakdowns"
          },
          {
            title: "Scene Planning",
            description: "AI analyzes script and generates detailed visual descriptions for each scene"
          },
          {
            title: "Image Generation",
            description: "GoogleFlow creates images for each scene based on visual descriptions"
          },
          {
            title: "Video Assembly",
            description: "Combines generated images with transitions, timing, and audio into final video"
          }
        ]
      },
      {
        title: "Technical Stack",
        type: "tech-list",
        items: [
          { name: "n8n", description: "Workflow orchestration and automation" },
          { name: "OpenAI", description: "Script generation and scene planning" },
          { name: "GoogleFlow", description: "AI image generation engine" },
          { name: "HTTP API", description: "Service integration and data flow" }
        ]
      },
      {
        title: "Generation Pipeline",
        type: "list",
        items: [
          "Topic input and prompt engineering",
          "Script creation with narrative structure",
          "Scene breakdown and visual planning",
          "AI image generation per scene",
          "Video compilation and rendering",
          "Quality checks and output delivery"
        ]
      },
      {
        title: "Image Generation Settings",
        type: "list",
        items: [
          "Consistent visual style across scenes",
          "Customizable aspect ratios and resolutions",
          "Scene-specific prompt optimization",
          "Character and setting continuity",
          "Automated prompt enhancement"
        ]
      },
      {
        title: "Use Cases",
        type: "list",
        items: [
          "Educational content creation",
          "Marketing video automation",
          "Social media content generation",
          "Storyboarding and prototyping",
          "Concept visualization"
        ]
      },
      {
        title: "Key Features",
        type: "list",
        items: [
          "End-to-end video generation automation",
          "AI-powered script and scene creation",
          "Multi-model integration (OpenAI + GoogleFlow)",
          "Scalable workflow with customizable parameters",
          "Consistent visual narrative across scenes"
        ]
      }
    ]
  },

  "English Learning Assistant": {
    sections: [
      {
        title: "Overview",
        content: "AI-powered English learning assistant using structured prompt templates with RAG for adaptive conversations and intent handling. Built on Dify platform with advanced conversation management and personalized learning paths.",
        stats: [
          { label: "Platform", value: "Dify" },
          { label: "AI Engine", value: "GPT-4.1-mini" },
          { label: "Method", value: "RAG + Prompts" }
        ]
      },
      {
        title: "Learning Workflow",
        type: "steps",
        steps: [
          {
            title: "Student Assessment",
            description: "AI evaluates current English level through conversation and diagnostic questions"
          },
          {
            title: "Lesson Planning",
            description: "RAG retrieves relevant learning materials and structures personalized lesson plans"
          },
          {
            title: "Interactive Practice",
            description: "Conversational practice with real-time feedback on grammar, vocabulary, and pronunciation"
          },
          {
            title: "Progress Tracking",
            description: "System tracks improvement over time and adjusts difficulty dynamically"
          }
        ]
      },
      {
        title: "Technical Stack",
        type: "tech-list",
        items: [
          { name: "Dify", description: "LLM application orchestration platform" },
          { name: "GPT-4", description: "Language model for conversations" },
          { name: "RAG", description: "Knowledge retrieval for learning materials" },
          { name: "Prompt Engineering", description: "Structured templates for teaching" },
          { name: "Conversation Memory", description: "Context and progress tracking" }
        ]
      },
      {
        title: "Key Features",
        type: "list",
        items: [
          "Adaptive learning difficulty based on student level",
          "Real-time grammar and vocabulary corrections",
          "Personalized conversation topics",
          "Progress tracking and analytics",
          "RAG-powered learning material retrieval",
          "Natural conversation flow with context awareness"
        ]
      },
      {
        title: "Conversation Variables",
        type: "list",
        items: [
          "Student English proficiency level",
          "Learning goals and focus areas",
          "Previous lesson history",
          "Common mistakes and improvement areas",
          "Preferred learning style and pace",
          "Topic interests for engagement"
        ]
      },
      {
        title: "Use Cases",
        type: "list",
        items: [
          "One-on-one English conversation practice",
          "Grammar and vocabulary building",
          "Business English training",
          "Exam preparation assistance",
          "Cultural language learning"
        ]
      },
      {
        title: "Prompt Engineering Approach",
        type: "list",
        items: [
          "System prompts define AI personality and teaching style",
          "Context injection includes student level and learning history",
          "Few-shot examples guide response format and tone",
          "Guardrails ensure educational focus and appropriate content",
          "Structured outputs for consistent lesson delivery"
        ]
      }
    ]
  },

  "WellTalk | Mental Health Web App": {
    sections: [
      {
        title: "Overview",
        content: "WellTalk is a web application designed to elevate student support and mental health services at CIT-University (Cebu Institute of Technology). The platform connects students with counselors and enables teachers to refer students who may need support.",
        stats: [
          { label: "Frontend", value: "React + Vite" },
          { label: "Backend", value: "Spring Boot" },
          { label: "Styling", value: "Tailwind CSS" }
        ]
      },
      {
        title: "Technical Stack",
        type: "tech-list",
        items: [
          { name: "React with Vite", description: "Modern frontend framework" },
          { name: "Tailwind CSS", description: "Utility-first styling" },
          { name: "Spring Boot (Java)", description: "Backend REST API" },
          { name: "Maven", description: "Build automation" },
          { name: "Node.js", description: "Runtime environment" },
          { name: "Docker", description: "Containerization" }
        ]
      },
      {
        title: "Counselor Features",
        type: "steps",
        steps: [
          {
            title: "Appointment Scheduling",
            description: "Manage counseling sessions with students, set availability, and track upcoming appointments"
          },
          {
            title: "Post Announcements",
            description: "Share updates, mental health resources, and interact with the student community"
          },
          {
            title: "Note Taking",
            description: "Document counseling sessions and maintain confidential student interaction records"
          },
          {
            title: "Schedule Management",
            description: "Announce available time slots to students and manage booking preferences"
          }
        ]
      },
      {
        title: "Teacher Features",
        type: "steps",
        steps: [
          {
            title: "Student Referral",
            description: "Submit referrals for students who may need counseling support with detailed context"
          },
          {
            title: "Referral Status Tracking",
            description: "Monitor the status of submitted referrals and receive updates from counselors"
          }
        ]
      },
      {
        title: "Project Structure",
        type: "list",
        items: [
          "Main application built with Spring Boot backend",
          "React frontend bundled with the backend",
          "Frontend source located in welltalkwebapp_frontend directory",
          "Dockerized for easy deployment and scalability",
          "Maven manages dependencies and build process"
        ]
      }
    ]
  },

  "Support Chatbot": {
    sections: [
      {
        title: "Overview",
        content: "AI-powered customer support chatbot handling FAQs and guidance with RAG-powered knowledge base integration. Built on Dify platform, it intelligently routes queries through intent classification and provides contextual responses.",
        stats: [
          { label: "Platform", value: "Dify" },
          { label: "AI Model", value: "OpenAI" },
          { label: "Backend", value: "CakePHP" }
        ]
      },
      {
        title: "Support Workflow",
        type: "steps",
        steps: [
          {
            title: "Intent Classification",
            description: "LLM analyzes user query to determine category: FAQ, usage guidance, campaign info, or technical issues"
          },
          {
            title: "Knowledge Retrieval (RAG)",
            description: "Searches internal knowledge base using semantic similarity with Cohere reranking for accuracy"
          },
          {
            title: "Conditional Routing",
            description: "Routes query to specialized response handlers based on intent classification results"
          },
          {
            title: "Response Generation",
            description: "Generates contextual responses using retrieved knowledge and conversation history"
          },
          {
            title: "Suggested Questions",
            description: "Provides follow-up question suggestions after each answer for better user engagement"
          }
        ]
      },
      {
        title: "Technical Stack",
        type: "tech-list",
        items: [
          { name: "Dify", description: "AI workflow orchestration platform" },
          { name: "OpenAI", description: "Language model for understanding and generation" },
          { name: "Cohere", description: "Multilingual reranking model" },
          { name: "RAG", description: "Knowledge base retrieval system" },
          { name: "CakePHP", description: "Backend integration framework" },
          { name: "LLM", description: "Intent classification and response routing" }
        ]
      },
      {
        title: "Key Features",
        type: "list",
        items: [
          "Intent-based query classification for accurate routing",
          "RAG-powered knowledge base retrieval with semantic search",
          "Multilingual reranking using Cohere for improved relevance",
          "Conditional workflow routing based on intent categories",
          "Context-aware responses using conversation history",
          "Automated suggested questions for user guidance",
          "Fallback handling for out-of-scope queries"
        ]
      },
      {
        title: "Intent Categories",
        type: "list",
        items: [
          "FAQ_USAGE: General product usage and how-to questions",
          "CAMPAIGN: Marketing campaigns and promotional inquiries",
          "TROUBLE_AGENT: Technical issues and troubleshooting",
          "OUT_OF_SCOPE: Unrelated or vague queries with fallback responses"
        ]
      },
      {
        title: "Retrieval Configuration",
        type: "list",
        items: [
          "Semantic search with vector embeddings",
          "Cohere rerank-multilingual-v3.0 for result optimization",
          "Confidence-based filtering for quality assurance",
          "Multi-stage retrieval with iterative refinement",
          "Knowledge base integration with CakePHP backend"
        ]
      }
    ]
  },

  "CI/CD Test Automation": {
    sections: [
      {
        title: "Overview",
        content: "Distributed test pipeline with GitHub Actions, database queue, and parallel PHP workers. Automates PHPUnit testing for pull requests with 5 parallel workers, reducing CI time by 50% through intelligent queueing and concurrent execution.",
        stats: [
          { label: "Workers", value: "5" },
          { label: "Check Interval", value: "2 min" },
          { label: "Time Reduction", value: "50%" }
        ]
      },
      {
        title: "CI/CD Pipeline",
        type: "steps",
        steps: [
          {
            title: "Label Trigger",
            description: "PR marked for review with 'php_unit_test' label, triggering GitHub Actions workflow"
          },
          {
            title: "Queue Management",
            description: "GitHub Action fetches PR details via API and saves to MySQL database for processing queue"
          },
          {
            title: "Worker Detection",
            description: "5 parallel workers with crons check database every 2 minutes for unprocessed test jobs"
          },
          {
            title: "Test Execution",
            description: "Worker checkouts PR branch, runs PHPUnit tests, and sends Slack notification with PR author"
          },
          {
            title: "Coverage Report",
            description: "Generates detailed code coverage report on separate server and sends comprehensive results to Slack"
          }
        ]
      },
      {
        title: "Technical Stack",
        type: "tech-list",
        items: [
          { name: "GitHub Actions", description: "CI/CD orchestration and triggers" },
          { name: "PHPUnit", description: "Unit testing framework" },
          { name: "MySQL", description: "Queue database for job management" },
          { name: "CakePHP", description: "Backend framework for workers" },
          { name: "Cron Jobs", description: "Scheduled worker polling (2-minute intervals)" },
          { name: "Slack API", description: "Real-time notifications and reports" }
        ]
      },
      {
        title: "Worker Architecture",
        type: "list",
        items: [
          "5 cloned repository workers deployed on dedicated server",
          "Each worker runs 5 simultaneous cron jobs for redundancy",
          "2-minute polling interval to check database queue",
          "First-come-first-served job claiming to prevent conflicts",
          "Isolated execution environments per worker",
          "Parallel processing reduces overall CI pipeline time by 50%"
        ]
      },
      {
        title: "Key Features",
        type: "list",
        items: [
          "Automated PR testing triggered by GitHub labels",
          "Distributed queue system with MySQL for job management",
          "Parallel execution across 5 workers for faster feedback",
          "Real-time Slack notifications for test initiation",
          "Detailed code coverage reports generated on separate server",
          "Comprehensive test results delivered via Slack",
          "Branch isolation with automatic checkout per PR",
          "Scalable architecture supporting multiple concurrent PRs"
        ]
      },
      {
        title: "Workflow Benefits",
        type: "list",
        items: [
          "50% faster CI time - Parallel worker execution",
          "Better resource utilization - Queue-based distribution",
          "Real-time visibility - Slack integration for updates",
          "Code quality assurance - Automated coverage reports",
          "Developer productivity - Quick feedback on PR changes"
        ]
      }
    ]
  }
};
